/*
 * Base route path for the native Airflow (Beta) dashboard — the legacy iframe-embedded
 * Airflow page still owns /airflow, so this module is mounted at /airflow-beta instead
 */
export const AIRFLOW_BASE_PATH = '/airflow-beta';

/*
 * DAG IDs with this prefix are managed by PI-integrator and cannot be deleted from this UI
 */
export const MODEL_REQUEST_DAG_PREFIX = 'model-request';

/*
 * Page size for the DAGs list table
 */
export const DAG_LIST_LIMIT = 10;

/*
 * Page size for server-paginated DAG run lists
 */
export const DAG_RUNS_LIMIT = 50;

/*
 * Page size for server-paginated task instance lists
 */
export const TASK_INSTANCES_LIMIT = 50;

/*
 * Client-side page size for the Tasks tab (fetches all task definitions, paginates locally)
 */
export const TASKS_TAB_PAGE_SIZE = 6;

/*
 * Client-side page size for a run's Task Instances tab
 */
export const RUN_TASK_INSTANCES_PAGE_SIZE = 25;

/*
 * Airflow's sentinel value for a task instance with no map index (non-mapped task)
 */
export const MAP_INDEX_NONE = -1;

/*
 * Number of points shown in the task-detail "recent task instances" duration chart
 */
export const RECENT_TASK_INSTANCES_CHART_LIMIT = 14;

/*
 * Max rows shown in the DAG Overview tab's Recent Failed Tasks list
 */
export const FAILED_TASK_LOGS_LIMIT = 10;

/*
 * Debounce delay for search inputs across the dashboard
 */
export const SEARCH_DEBOUNCE_MS = 400;

export const DAG_DETAIL_TAB = {
    overview: 'overview',
    runs: 'runs',
    tasks: 'tasks',
    calendar: 'calendar',
    code: 'code',
    details: 'details',
} as const;

export type DagDetailTab = (typeof DAG_DETAIL_TAB)[keyof typeof DAG_DETAIL_TAB];

/*
 * Ordered tab list for the DAG detail screen's tab bar
 */
export const DAG_DETAIL_TABS: { value: DagDetailTab; label: string }[] = [
    { value: DAG_DETAIL_TAB.overview, label: 'Overview' },
    { value: DAG_DETAIL_TAB.runs, label: 'Runs' },
    { value: DAG_DETAIL_TAB.tasks, label: 'Tasks' },
    { value: DAG_DETAIL_TAB.calendar, label: 'Calendar' },
    { value: DAG_DETAIL_TAB.code, label: 'Code' },
    { value: DAG_DETAIL_TAB.details, label: 'Details' },
];

export const DAG_RUN_DETAIL_TAB = {
    taskInstances: 'task-instances',
    code: 'code',
    details: 'details',
} as const;

export type DagRunDetailTab = (typeof DAG_RUN_DETAIL_TAB)[keyof typeof DAG_RUN_DETAIL_TAB];

/*
 * Ordered tab list for the DAG-run detail screen's tab bar
 */
export const DAG_RUN_DETAIL_TABS: { value: DagRunDetailTab; label: string }[] = [
    { value: DAG_RUN_DETAIL_TAB.taskInstances, label: 'Task Instances' },
    { value: DAG_RUN_DETAIL_TAB.code, label: 'Code' },
    { value: DAG_RUN_DETAIL_TAB.details, label: 'Details' },
];

export const DAG_TASK_DETAIL_TAB = {
    overview: 'overview',
    taskInstances: 'task-instances',
} as const;

export type DagTaskDetailTab = (typeof DAG_TASK_DETAIL_TAB)[keyof typeof DAG_TASK_DETAIL_TAB];

/*
 * Ordered tab list for the DAG-task detail screen's tab bar
 */
export const DAG_TASK_DETAIL_TABS: { value: DagTaskDetailTab; label: string }[] = [
    { value: DAG_TASK_DETAIL_TAB.overview, label: 'Overview' },
    { value: DAG_TASK_DETAIL_TAB.taskInstances, label: 'Task Instances' },
];

export const DAG_RUN_TASK_DETAIL_TAB = {
    logs: 'logs',
    xcom: 'xcom',
    assetEvents: 'asset-events',
    code: 'code',
    details: 'details',
} as const;

export type DagRunTaskDetailTab = (typeof DAG_RUN_TASK_DETAIL_TAB)[keyof typeof DAG_RUN_TASK_DETAIL_TAB];

/*
 * Ordered tab list for the DAG-run-task detail screen's tab bar
 */
export const DAG_RUN_TASK_DETAIL_TABS: { value: DagRunTaskDetailTab; label: string }[] = [
    { value: DAG_RUN_TASK_DETAIL_TAB.logs, label: 'Logs' },
    { value: DAG_RUN_TASK_DETAIL_TAB.xcom, label: 'XCom' },
    { value: DAG_RUN_TASK_DETAIL_TAB.assetEvents, label: 'Asset Events' },
    { value: DAG_RUN_TASK_DETAIL_TAB.code, label: 'Code' },
    { value: DAG_RUN_TASK_DETAIL_TAB.details, label: 'Details' },
];

/*
 * Time-range options driving the Overview tabs' stat cards and sparklines
 */
export const TIME_RANGE_OPTIONS: { value: string; label: string; hours: number }[] = [
    { value: '1h', label: 'Last 1 Hour', hours: 1 },
    { value: '12h', label: 'Last 12 Hours', hours: 12 },
    { value: '24h', label: 'Last 24 Hours', hours: 24 },
    { value: '7d', label: 'Last 7 Days', hours: 168 },
    { value: '14d', label: 'Last 14 Days', hours: 336 },
    { value: '30d', label: 'Last 30 Days', hours: 720 },
];

export const DEFAULT_TIME_RANGE = '24h';

export const RUNS_CHART_LIMIT_OPTIONS = [10, 25, 50, 100] as const;

export const DEFAULT_RUNS_CHART_LIMIT = 10;

export const PAUSED_FILTER = {
    all: 'all',
    active: 'active',
    paused: 'paused',
} as const;

export type PausedFilter = (typeof PAUSED_FILTER)[keyof typeof PAUSED_FILTER];

/*
 * Maps the paused-filter UI value to the API's boolean `paused` param
 */
export const PAUSED_FILTER_VALUE: Record<PausedFilter, boolean | undefined> = {
    [PAUSED_FILTER.all]: undefined,
    [PAUSED_FILTER.active]: false,
    [PAUSED_FILTER.paused]: true,
};

export const PAUSED_FILTER_TABS: { value: PausedFilter; label: string }[] = [
    { value: PAUSED_FILTER.all, label: 'All' },
    { value: PAUSED_FILTER.active, label: 'Active' },
    { value: PAUSED_FILTER.paused, label: 'Paused' },
];

export const DAG_RUN_STATE_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'queued', label: 'Queued' },
    { value: 'running', label: 'Running' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
];

export const RUN_TYPE_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'All Types' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'manual', label: 'Manual' },
    { value: 'backfill', label: 'Backfill' },
    { value: 'asset_triggered', label: 'Asset Triggered' },
];

export const TASK_INSTANCE_STATE_FILTERS: { value: string; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'success', label: 'Success' },
    { value: 'running', label: 'Running' },
    { value: 'failed', label: 'Failed' },
    { value: 'queued', label: 'Queued' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'deferred', label: 'Deferred' },
    { value: 'up_for_retry', label: 'Up For Retry' },
    { value: 'upstream_failed', label: 'Upstream Failed' },
    { value: 'skipped', label: 'Skipped' },
];

// "run_after" is an Airflow 3.x-era field name (another V2 assumption that doesn't hold for
// 2.6.0) — execution_date is the real, documented sort field for DAG runs in this version.
export const RUN_SORT_OPTIONS: { value: string; label: string }[] = [
    { value: '-execution_date', label: 'Execution Date (Newest)' },
    { value: 'execution_date', label: 'Execution Date (Oldest)' },
    { value: 'dag_run_id', label: 'Run ID' },
];

export const RUN_LIMITS = [10, 25, 50, 100] as const;

// Airflow's real /dags endpoint doesn't support sorting by last-run fields (that was an
// unverified assumption carried over from V2) — dag_id is a documented, always-valid sort field.
export const DAG_ORDER_BY = 'dag_id';

export const DAG_RUNS_ORDER_BY = '-execution_date';

// "-id" isn't a real TaskInstance field in Airflow 2.6.0 (no "id" in the schema) —
// execution_date is the confirmed, always-present sort field, same as DAG runs.
export const TASK_INSTANCES_ORDER_BY = '-execution_date';

/*
 * Remaps a DataTable column id to the API's order_by field, for columns where they differ
 */
export const DAG_SORT_COLUMN_TO_API_FIELD: Record<string, string> = {
    next_dagrun_logical_date: 'next_dagrun',
};

/*
 * Builds the toast message shown after successfully deleting a DAG
 */
export function dagDeletedToastMessage(dagId: string) {
    return `Deleted DAG "${dagId}". It may take up to 30 seconds to reflect.`;
}
